// The selected object's handles in the 3D view and in the section views: the
// targets, the press, the step and its typed figure, the readout, the paint.
// docs/claude/handles.md.
import {
  type Connection,
  type Polygon,
  type ProductInstance,
  type Section3d,
  type SectionLine,
  type Vec2,
  type Wall,
} from '../../shared/types';
import {
  drawingRev,
  dropDrag,
  flashToolStatus,
  instLocked,
  invalidate,
  objUnpickable,
  renderProperties,
  startDrag,
  state,
  updateStatus,
  vp,
} from './core';
import {
  drawingDimStyle,
  formatCoordPair,
  formatDimension,
} from '../dimFormat';
import { roomDoors } from '../door';
import { gridStepMm } from '../geometry';
import {
  connHandles,
  DECIDE_PX,
  drawGuideLines,
  drawHandleMarks,
  featureFaintGuides,
  featureGuides,
  featureHandles,
  figureZero,
  firstMoveAxis,
  type Handle as ObjHandle,
  HANDLE_LABELS,
  type HandleLive,
  handleMarkAt,
  handlePitch,
  handlesInView,
  type InView,
  type NamedHeight,
  nearestPoint,
  partHandles,
  polygonHandles,
  type Pt3,
  rayToAxis,
  rayToLevel,
  rayToVertical,
  roomHandles,
  roundPlanDelta,
  snapFigure,
  wallHandles,
} from '../handles';
import { type HitOf } from '../handleHits';
import { tr } from '../i18n';
import {
  contentMemo,
  HEAVY_FIELDS,
} from '../plainData';
import {
  type SecPart,
  sectionFrame,
  sectionFramePoint,
  sectionSourceBoxes,
  sectionVertices,
  sectionView,
  srcOf,
} from '../section';
import { type GizmoArrow } from '../gizmo';
import { featureTargets } from '../roomFeatures';
import { activeCagesRev } from '../sculptBench';
import { section3dHandles } from '../section3d';
import {
  MULTI_KINDS,
  selectedCount,
  selectedOf,
} from '../selection';
import { type StepEnd } from '../undoHistory';
import { type UnderWayRow } from '../underWay';
import {
  rebuildObjects3d,
  refreshView3d,
  syncView3dSection,
  view3dHiddenCategories,
  viewer3d,
} from './view3d';
import {
  frameMove,
  fromPlaneFrame,
  type WorkPlane,
} from '../workPlane';
import {
  $,
  canvas,
  ctx,
  editRoomTop,
  gestureSetbacks,
  hostRoomOf,
  liveFeatureNow,
  productOf,
  regenerateSauna,
  screenPos,
  sectionSources,
  selectedInstance,
  selectedWallFeature,
  templatesRevision,
  type UndoMark,
  undoSteps,
  viewW,
} from '../main';
import { activeSheet } from './sheetSpace';
import {
  clearGripFlash,
  flashOnce,
} from './ceilingUi';
import { updateDistEntry } from './distanceEntry';
import { parseMeasure } from './panelRows';
import { endPressWork } from './grips';
import { cancelHeldUnderWay } from './underWayUi';
import { tool3dRow } from './tools3d';
import {
  type MoveSel,
  paintGizmo3d,
  startArrowWork3d,
  startRingWork3d,
} from './moveGizmo';
import { type Pointer3dHit } from '../pointer3d';
import {
  connEdit,
  connGeomOf,
  selectedConnection,
} from './connectionUi';

/** What a handle works on, as it is now: an instance (a room — its own handles or
 *  one of its features' —, a part, a node object), a drawn wall or line (or one
 *  of its features), an extruded shape. */
type HandleObj =
  | Connection
  | MoveSel
  | Polygon
  | ProductInstance
  | Section3d
  | Wall;
export type HandleTarget =
  | { kind: 'conn'; obj: Connection; part?: undefined }
  | { kind: 'inst'; obj: ProductInstance; part?: SecPart }
  | { kind: 'move'; obj: MoveSel; part?: undefined }
  | { kind: 'poly'; obj: Polygon; part?: undefined }
  | { kind: 'section3d'; obj: Section3d; part?: undefined }
  | { kind: 'wall'; obj: Wall; part?: SecPart };
/** A handle of any of them: its setters take the object it was built for. */
type AnyHandle = ObjHandle<HandleObj>;

/** What a work is beyond the handle it moves (`HandleWork.rule`): what it works
 *  on, where its figure comes from and how it snaps, a typed figure, what a step
 *  rebuilds live, its prompt and its chip — and, the move gizmo's, a step of its
 *  own. An object's handles have `HANDLE_RULE`; the gizmo's arrows and ring
 *  supply theirs (moveGizmo.ts `gizmoRule`), so no step of the work names the
 *  gizmo. */
export interface WorkRule {
  /** What it works on while it is still the one worked: null ends the work. */
  target(w: HandleWork): HandleTarget | null;
  /** The figure under the pointer in 3D, before its snap. Absent: the height on
   *  the handle's line (`handleHeight3d`: up, or along its `along`). */
  figure3d?(w: HandleWork): number | null;
  /** The figure written for a raw one, and the height it caught (named in the chip). */
  snap(w: HandleWork, raw: number): { v: number; caught: NamedHeight | null };
  /** A typed figure: the value its height takes, or why it cannot be read.
   *  Absent: a handle's own reading (its height, else its plan figure). */
  typed?(text: string): { v: number } | { refusal: string };
  /** What a step builds again live in the 3D view (with what follows it). */
  ids(t: HandleTarget): readonly string[];
  /** The bar's prompt (named `hint`: the one-dialog check, wiring.test.mjs,
   *  takes a call of that name for the native dialog). */
  hint(w: HandleWork): string;
  /** The chip's lines. */
  readout(w: HandleWork, t: HandleTarget): string[];
  /** Its step is its own (the gizmo's move session: the copy, the re-anchoring
   *  and the layers let go land inside it): the work ends it there, never
   *  through `endPressWork`. */
  endStep?(how: StepEnd): void;
  /** The move gizmo's selection, which its overlay paints lit (moveGizmo.ts `paintGizmo3d`). */
  gizmo?: MoveSel;
}

/** A handle being worked. The drawing as it was at the press is kept, so a
 *  cancel puts it back. */
export interface HandleWork {
  space: '3d' | 'section';
  /** The section whose view it is worked in. */
  sectionId?: string;
  /** The object it works on, and the feature of it (absent: the object's own handle). */
  ref: { kind: HandleTarget['kind']; id: string; part?: SecPart };
  /** The handle as built at the press: its setters measure from there. */
  hd: AnyHandle;
  /** What the work is beyond its handle: an object's handle's, or the gizmo's. */
  rule: WorkRule;
  /** The candidate point it is drawn at (3D), and where it stood (world plan). */
  pick: number;
  at: Vec2;
  /** Screen px of the press and of the pointer now (client px in 3D). */
  press: Vec2;
  pointer: Vec2;
  alt: boolean;
  /** A click armed it: it follows the pointer, and a click drops it. */
  armed: boolean;
  /** The axis it works: a one-axis handle's own, else the first move's. */
  mode: 'height' | 'plan' | null;
  /** Whether it may move in plan here (in a section: only along the view's line). */
  planOk: boolean;
  /** Where its height rises on screen (per 100 mm), and screen px per mm of
   *  height and of plan there. */
  up: Vec2;
  pxPerMm: number;
  planPxPerMm: number;
  /** The pointer at the press: 3D — its height on the handle's vertical line and
   *  its point on the handle's level; a section — its (t, h) in the view. */
  zRef: number | null;
  planRef: Vec2 | null;
  /** A section's other heights within reach: the view's own vertices (t, and mm
   *  above the figure's zero — `figureZero`), the object's own left out. */
  viewSnaps: Array<{ t: number; z: number }>;
  /** The figure last written and the height it caught. */
  value: number | null;
  caught: NamedHeight | null;
  moved: boolean;
  /** A pointer move waits for the next frame. */
  dirty: boolean;
  /** Its step, begun at the press: a cancel puts the drawing back there, and a
   *  drawing replaced under it (a load) ends the work. */
  start: UndoMark;
}
export let handleWork: HandleWork | null = null;

/** Whose handles show: ONE object alone in the selection, in Select, that may be
 *  edited — a sauna or steam room (its own, or those of the ONE feature of it
 *  picked, `liveFeatureNow`: a bench, a pane, an opening, a door, a partition, a
 *  heater wall, a sculpted bench's cage — a support and any other fixture have
 *  none —; none for several panes or
 *  openings, nor in its vertex edit), a part or a node object, a drawn wall or
 *  line (or the feature of it picked), an extruded shape (neither in its vertex
 *  edit). */
function handleTarget(): HandleTarget | null {
  // A tool whose row shows the 3D section (the 3D section's): its own handle.
  if (tool3dRow()?.section) {
    const sec = state.drawing.section3d;
    return sec && state.viewTab === '3d' ? { kind: 'section3d', obj: sec } : null;
  }
  if (state.tool !== 'select') return null;
  const editable = (r: ProductInstance | null | undefined): r is ProductInstance => !!r?.autoBenches && !instLocked(r) && state.polyRoomVertexEditId !== r.id;
  const conn = selectedConnection();
  if (conn) return objUnpickable(conn.id) ? null : { kind: 'conn', obj: conn };
  const n = selectedCount(state, MULTI_KINDS);
  const f = liveFeatureNow();
  if (f) {
    // A bench picked in 3D or in a section selects the bench alone.
    const [t, more] = featureTargets(f);
    if (!editable(f.room) || more || n > 1 || (n === 1 && state.selectedId !== f.room.id)) return null;
    return { kind: 'inst', obj: f.room, part: t.part };
  }
  if (n !== 1) return null;
  const inst = selectedInstance();
  if (inst) return (inst.autoBenches ? !editable(inst) : instLocked(inst)) ? null : { kind: 'inst', obj: inst };
  const off = (id: string): boolean => objUnpickable(id) || state.editVerticesId === id;
  const wid = selectedOf(state, 'wall')[0], wall = wid ? (state.drawing.walls || []).find((x) => x.id === wid) : undefined;
  if (wall) {
    if (off(wall.id)) return null;
    const wf = selectedWallFeature();
    return { kind: 'wall', obj: wall, part: wf ? { kind: 'wallFeature', id: wf.f.id } : undefined };
  }
  const pid = selectedOf(state, 'polygon')[0], poly = pid ? (state.drawing.polygons || []).find((p) => p.id === pid) : undefined;
  return poly && !off(poly.id) ? { kind: 'poly', obj: poly } : null;
}

const partKey = (p: SecPart | undefined): string => (p ? `${p.kind}:${p.id}` : '');

/** An object's handle's rule (`WorkRule`): its target is the one whose handles
 *  show, its height snaps to what it knows (`snapHandleFigure`), it rebuilds its
 *  object, and its prompt and chip name its figure. */
const HANDLE_RULE: WorkRule = {
  target: objectTarget,
  snap: snapHandleFigure,
  ids: (t) => [t.obj.id],
  hint: objectPrompt,
  readout: handleReadout,
};

/** What a handle is worked on, while it is still the one worked (its rule's
 *  `target`). */
export function workedTarget(w: HandleWork): HandleTarget | null {
  return w.rule.target(w);
}

/** An object's handle: what it works on is still the one whose handles show. */
function objectTarget(w: HandleWork): HandleTarget | null {
  const t = handleTarget();
  return t && t.kind === w.ref.kind && t.obj.id === w.ref.id && partKey(t.part) === partKey(w.ref.part) ? t : null;
}

/** A target's handles, built once per state of what they read — the object, the
 *  room a part stands in, the catalogue (a part's defaults): a drag rebuilds them
 *  each frame. */
/** An object's JSON less what no handle reads: a product's drawing, image and
 *  data (megabytes for a part drawn from a DXF, read every frame). */
/** The worked target's handles, kept while what they stand on holds: the
 *  target, its host room, a connection's ends, the catalogue (plainData.ts). */
const HANDLES = contentMemo<AnyHandle[]>({ max: 1, skip: HEAVY_FIELDS });
/** How many times the handles were made again (the section views' handles follow). */
let handlesMade = 0;
function targetHandles(t: HandleTarget): AnyHandle[] {
  const host = t.kind === 'inst' && !t.obj.autoBenches ? hostRoomOf(t.obj) : null;
  // A connection's handles stand where its ends' objects (and, a schematic one's
  // run, the walls) put them.
  const g = t.kind === 'conn' ? connGeomOf(t.obj) : null;
  const ends = g ? [g.ctrl, g.run, g.pipe && [g.pipe.slope, g.pipe.flexible]] : null;
  return HANDLES.get('handles', () => [t.kind, partKey(t.part), t.obj, host, ends, templatesRevision, activeCagesRev()], () => { handlesMade++; return handlesFor(t, host); });
}
/** What the target's handles stand on, drawn with them (3D lines, world): a
 *  sculpted bench's cage. */
function targetGuides(t: HandleTarget): Pt3[][] {
  return t.kind === 'inst' && t.part ? featureGuides(t.obj, t.part) : [];
}
/** What is drawn faint beside them: a sculpted bench's other cages. */
function targetFaintGuides(t: HandleTarget): Pt3[][] {
  return t.kind === 'inst' && t.part ? featureFaintGuides(t.obj, t.part) : [];
}
function handlesFor(t: HandleTarget, host: ProductInstance | null): AnyHandle[] {
  if (t.kind === 'move') return []; // a gizmo's arrows are its own (moveGizmo.ts)
  if (t.kind === 'conn') { const g = connGeomOf(t.obj); return g ? connHandles(g, { edit: connEdit, geom: connGeomOf }) : []; }
  if (t.kind === 'section3d') return section3dHandles(t.obj);
  if (t.kind === 'wall') return wallHandles(t.obj, t.part);
  if (t.kind === 'poly') return polygonHandles(t.obj);
  if (!t.obj.autoBenches) return partHandles(t.obj, host, productOf(t.obj));
  return t.part ? featureHandles(t.obj, t.part) : roomHandles(t.obj);
}

/** The partition a room's pane, opening or door stands in, if any. */
function featureHost(room: ProductInstance, part: SecPart): string | undefined {
  if (part.kind === 'pane') return room.glass?.find((g) => g.id === part.id)?.innerWallId;
  if (part.kind === 'opening') return room.openings?.find((o) => o.id === part.id)?.innerWallId;
  if (part.kind === 'door') return roomDoors(room).find((d) => d.id === part.id)?.innerWallId;
  return undefined;
}

/** A new handle's work, its gesture begun (`start`: a gizmo's, by its move session). */
export function startHandleWork(w: Omit<HandleWork, 'armed' | 'mode' | 'value' | 'caught' | 'moved' | 'dirty' | 'start' | 'pointer'>, start = undoSteps.begin()): void {
  clearGripFlash();
  state.toolStatusFlash = null; // a refusal left from an earlier work gives way to this one's prompt
  handleWork = { ...w, pointer: { ...w.press }, armed: false, mode: null, value: null, caught: null, moved: false, dirty: false, start };
  w.hd.pick?.();
  updateStatus();
  invalidate();
}

/** Write a handle's height figure (and re-lay the room when its Height moved). */
function writeHandleHeight(t: HandleTarget, w: HandleWork, v: number, caught: NamedHeight | null): void {
  const h = w.hd.height;
  if (!h) return;
  flashOnce(t.kind === 'inst' ? editRoomTop(t.obj, () => h.set(t.obj, v)) : h.set(t.obj, v));
  if (h.regenerate && t.kind === 'inst') regenerateSauna(t.obj);
  w.value = v;
  w.caught = caught;
  w.moved = true;
}

/** Apply the latest pointer to the worked handle — at most once a frame. Nothing
 *  moves until the pointer has gone `DECIDE_PX` from the press; a handle that
 *  moves both ways is then fixed to the axis its first move took. */
export function stepHandleWork(): void {
  const w = handleWork;
  if (!w || !w.dirty) return;
  w.dirty = false;
  const t = workedTarget(w);
  if (!t) { finishHandleWork(); return; }
  if (!w.mode) {
    const mv = { x: w.pointer.x - w.press.x, y: w.pointer.y - w.press.y };
    if (w.hd.height && w.hd.plan && w.planOk) w.mode = firstMoveAxis(mv, w.up);
    else if (Math.hypot(mv.x, mv.y) >= DECIDE_PX) w.mode = w.hd.height ? 'height' : 'plan';
    if (!w.mode) return;
  }
  if (w.mode === 'height' && w.hd.height) {
    const raw = w.space === '3d' ? (w.rule.figure3d ?? handleHeight3d)(w) : handleHeightInView(w);
    if (raw == null) return;
    const s = w.rule.snap(w, raw);
    writeHandleHeight(t, w, s.v, s.caught);
  } else if (w.mode === 'plan' && w.hd.plan) {
    const d = w.space === '3d' ? handlePlan3d(w) : handlePlanInView(w);
    if (!d) return;
    const sec = w.space === 'section' ? (state.drawing.sections || []).find((s) => s.id === w.sectionId) : undefined;
    const axis = w.hd.plan.axis ?? (sec ? sectionFrame(sec).u : undefined);
    const step = w.alt ? 1 : gridStepMm(w.planPxPerMm);
    flashOnce(w.hd.plan.set(t.obj, roundPlanDelta(d, axis, t.kind === 'inst' ? t.obj.rotation : 0, step), w.alt ? 0 : 12 / w.planPxPerMm, sec ? sectionFrame(sec).u : undefined));
    if (w.hd.kind === 'setback') gestureSetbacks.delete(t.obj.id); // a fit later in this gesture starts from here
    w.moved = true;
  } else return;
  if (w.space === '3d' && t.kind === 'section3d') syncView3dSection(); // the clip moves, nothing is rebuilt
  else if (w.space === '3d') rebuildObjects3d(w.rule.ids(t));
  invalidate();
}

/** An object's handle's height snap: the heights it knows and a section view's
 *  own vertices within 12 px, else the zoom's round step (Alt: whole mm). */
function snapHandleFigure(w: HandleWork, raw: number): { v: number; caught: NamedHeight | null } {
  const s = snapFigure(raw, [...(w.hd.height?.snaps ?? []), ...w.viewSnaps], 12 / w.pxPerMm, gridStepMm(w.pxPerMm), w.alt);
  return { v: s.v, caught: s.caught ?? null };
}

/** Enter on a typed figure while a handle is worked: its height — or, once it
 *  has moved in plan (a plan-only handle at once), its plan figure: one, or an
 *  apex's two as x,y — absolute, through parseMeasure; then it is done. */
export function commitHandleEntry(): void {
  const w = handleWork, text = state.distEntry;
  state.distEntry = null;
  updateDistEntry();
  if (!w || text == null) return;
  const t = workedTarget(w);
  if (!t) { finishHandleWork(); return; }
  const fig = w.hd.plan?.figure;
  const typed = w.rule.typed?.(text);
  if (typed) {
    if ('refusal' in typed) { flashToolStatus(typed.refusal); return; }
    writeHandleHeight(t, w, typed.v, null);
  } else if (w.hd.height && !(w.mode === 'plan' && fig)) {
    const v = parseMeasure(text);
    if (v == null) { flashToolStatus(tr('Type the figure in mm, or with a unit (cm / m / ft / in).')); return; }
    writeHandleHeight(t, w, v, null);
  } else if (fig) {
    const vs = (fig.values.length > 1 ? text.split(/[,;]/) : [text]).map((s) => parseMeasure(s.trim()));
    if (vs.length !== fig.values.length || vs.some((v) => v == null)) {
      flashToolStatus(fig.values.length > 1 ? tr('Type the two figures as x,y in mm.') : tr('Type the figure in mm, or with a unit (cm / m / ft / in).'));
      return;
    }
    flashOnce(fig.set(t.obj, vs as number[]));
    if (w.hd.kind === 'setback') gestureSetbacks.delete(t.obj.id);
    w.moved = true;
  } else return;
  finishHandleWork();
}

function endHandleWork(how: StepEnd): boolean {
  const w = handleWork, own = w?.rule.endStep;
  // A work whose step is its own (the gizmo's move session) ends it there.
  if (w && own) {
    handleWork = null;
    own(how);
    afterHandleWork(w);
    return true;
  }
  return endPressWork(handleWork, how, () => { handleWork = null; }, afterHandleWork);
}
/** The worked handle is done: one undo step, from the press, when it changed
 *  anything. */
export function finishHandleWork(): void { endHandleWork('keep'); }
/** Esc (or undo): the worked handle goes back where it was, nothing recorded.
 *  True when one was worked. */
export function cancelHandleWork(): boolean { return endHandleWork('cancel'); }
/** The drawing was replaced under a worked handle (a load): its work just ends. */
export function dropHandleWork(): void { endHandleWork('drop'); }

/** The space a work is worked in, as the gesture under way names it. */
const spaceOf = (w: HandleWork): 'plan' | '3d' => (w.space === '3d' ? '3d' : 'plan');

/** A handle's work — the move gizmo's arrows and ring included — in the gesture
 *  under way (underWay.ts): held or armed, in 3D or in a section view. Its moves
 *  are the pointer it reads (applied once a frame); a press drops an armed one
 *  where it is pressed and is swallowed by a held one; Esc, Undo, a tab switch,
 *  closing the 3D view and a lost pointer (held) put it back. */
export const HANDLE_UNDER_WAY: UnderWayRow = {
  live: () => !!handleWork,
  armed: () => !!handleWork?.armed,
  space: () => (handleWork ? spaceOf(handleWork) : 'plan'),
  move: (p) => {
    const w = handleWork;
    if (!w || p.space !== spaceOf(w)) return false;
    w.pointer = p.at;
    w.alt = p.alt;
    w.dirty = true;
    if (w.space === 'section') invalidate();
    return true;
  },
  press: (p) => {
    const w = handleWork;
    if (!w || p.space !== spaceOf(w)) return false;
    if (w.armed) {
      w.pointer = p.at;
      w.alt = p.alt;
      w.dirty = true;
      stepHandleWork();
      finishHandleWork();
    }
    return true;
  },
  finish: () => endHandleWork('keep'),
  cancel: () => endHandleWork('cancel'),
  drop: dropHandleWork,
  check: () => {
    // A handle whose object is no longer the one worked (deleted, deselected) is done.
    if (handleWork && !undoSteps.live(handleWork.start)) dropHandleWork();
    if (handleWork && !workedTarget(handleWork)) finishHandleWork();
  },
  typed: commitHandleEntry,
  hint: () => (handleWork ? handlePrompt(handleWork) : null),
};

function afterHandleWork(w: HandleWork): void {
  if (state.dragMode === 'handle') dropDrag();
  if (state.distEntry != null) { state.distEntry = null; updateDistEntry(); }
  if (w.space === '3d') {
    $('view3d-canvas').style.cursor = '';
    if (w.ref.kind === 'section3d') syncView3dSection();
    else refreshView3d(false); // what follows the object (sceneChanges.ts) is built again
    viewer3d?.redraw();
  } else canvas.style.cursor = '';
  renderProperties();
  updateStatus();
  invalidate();
}

/** The prompt while a handle is worked (its rule's). */
export function handlePrompt(w: HandleWork): string {
  return w.rule.hint(w);
}

/** An object's handle's prompt: what it sets, and how. */
function objectPrompt(w: HandleWork): string {
  const label = tr(w.mode === 'plan' || !w.hd.height ? w.hd.plan?.figure?.label ?? HANDLE_LABELS[w.hd.kind] : w.hd.height.label);
  return (w.armed
    ? tr('{label} — move to set it, click to drop it, or type a figure and press Enter (Esc cancels)')
    : tr('{label} — drag to set it, or type a figure and press Enter (Esc cancels)')).replace('{label}', label);
}

/** What the worked handle reads beside it: its figure, the height it caught, and
 *  the pitch while a room's eave or crown moves. */
function handleReadout(w: HandleWork, t: HandleTarget): string[] {
  const style = drawingDimStyle(state.drawing);
  const live = targetHandles(t).find((k) => k.key === w.hd.key);
  if (w.mode === 'plan' || !w.hd.height) {
    const r = (live ?? w.hd).plan?.readout(t.obj);
    if (!r) return [];
    return [`${tr(r.label)} ${r.values.length === 2 ? formatCoordPair(r.values[0], r.values[1], style) : formatDimension(r.values[0], style)}`];
  }
  const h = live?.height ?? w.hd.height;
  const v = live?.height?.value ?? w.value ?? h.value;
  const caught = w.caught && Math.abs(w.caught.z - v) < 0.5 ? w.caught : null;
  const out = [`${tr(h.label)} ${formatDimension(v, style)}${caught?.name ? ` — ${tr(caught.name)}` : ''}`];
  if (t.kind === 'inst' && t.obj.autoBenches && (h.label === HANDLE_LABELS.crown || h.label === HANDLE_LABELS.eave)) {
    const p = handlePitch(t.obj);
    if (p != null) out.push(`${tr(HANDLE_LABELS.pitch)} ${p.toFixed(1)}°`);
  }
  return out;
}

// -- In 3D --

/** A 3D handle as drawn: the point it stands at (world plan) and on the canvas (css px). */
export interface Handle3d { hd: AnyHandle; p: Vec2; at: Vec2 }

/** The 3D view's handles as drawn now — the ONE list the overlay paints and a
 *  press hits: each of the target's handles at its candidate nearest the eye (the
 *  worked one at the candidate it was pressed on). */
function view3dHandles(): Handle3d[] {
  if (!viewer3d || state.viewTab !== '3d') return [];
  const w = handleWork?.space === '3d' ? handleWork : null;
  const t = w ? workedTarget(w) : handleTarget();
  if (!t || (t.kind === 'inst' && view3dHiddenCategories().has(t.obj.category))) return [];
  const eye = viewer3d.eye();
  const out: Handle3d[] = [];
  for (const hd of targetHandles(t)) {
    const p = (w && hd.key === w.hd.key ? hd.points[w.pick] : null) ?? nearestPoint(hd.points, hd.z, eye);
    const at = p ? viewer3d.project({ x: p.x, y: p.y, z: hd.z }) : null;
    if (p && at) out.push({ hd, p, at });
  }
  return out;
}

/** The 3D handle under a client point. */
export function view3dHandleAt(clientX: number, clientY: number): Handle3d | null {
  const r = $('view3d-canvas').getBoundingClientRect();
  const list = view3dHandles();
  const i = handleMarkAt(list, { x: clientX - r.left, y: clientY - r.top });
  return i >= 0 ? list[i] : null;
}

/** The height on the handle's vertical line under the pointer, as its figure. */
function handleHeight3d(w: HandleWork): number | null {
  if (!viewer3d || w.zRef == null || !w.hd.height) return null;
  const r = viewer3d.rayAt(w.pointer.x, w.pointer.y), ax = w.hd.height.along;
  const z = ax ? rayToAxis(r.o, r.d, { x: w.at.x, y: w.at.y, z: w.hd.z }, ax) : rayToVertical(r.o, r.d, w.at);
  return z == null ? null : w.hd.height.value + (z - w.zRef);
}

/** How far the pointer moved the handle on its level since the press (world plan). */
function handlePlan3d(w: HandleWork): Vec2 | null {
  if (!viewer3d || !w.planRef) return null;
  const r = viewer3d.rayAt(w.pointer.x, w.pointer.y);
  const p = rayToLevel(r.o, r.d, w.hd.z);
  return p ? { x: p.x - w.planRef.x, y: p.y - w.planRef.y } : null;
}

/** A press on a 3D handle: its work begins. */
function startHandle3d(m: Handle3d, e: PointerEvent): void {
  const v = viewer3d, t = handleTarget();
  if (!v || !t) return;
  const at3 = (dx: number, dy: number, dz: number): Vec2 | null => v.project({ x: m.p.x + dx, y: m.p.y + dy, z: m.hd.z + dz });
  const px = (q: Vec2 | null): number => (q ? Math.hypot(q.x - m.at.x, q.y - m.at.y) / 100 : 0);
  // Where the figure grows: up, or along a section's normal.
  const ax = m.hd.height?.along;
  const above = ax ? at3(ax.x * 100, ax.y * 100, ax.z * 100) : at3(0, 0, 100);
  const ray = v.rayAt(e.clientX, e.clientY);
  startHandleWork({
    space: '3d', ref: { kind: t.kind, id: t.obj.id, part: t.part }, hd: m.hd, rule: HANDLE_RULE, pick: Math.max(0, m.hd.points.indexOf(m.p)), at: m.p,
    press: { x: e.clientX, y: e.clientY }, alt: e.altKey, planOk: true,
    up: above ? { x: above.x - m.at.x, y: above.y - m.at.y } : { x: 0, y: 0 },
    pxPerMm: Math.max(1e-3, px(above)), planPxPerMm: Math.max(1e-3, (px(at3(100, 0, 0)) + px(at3(0, 100, 0))) / 2),
    zRef: ax ? rayToAxis(ray.o, ray.d, { x: m.p.x, y: m.p.y, z: m.hd.z }, ax) : rayToVertical(ray.o, ray.d, m.p),
    planRef: rayToLevel(ray.o, ray.d, m.hd.z), viewSnaps: [],
  });
  viewer3d?.setHover(null);
  $('view3d-canvas').style.cursor = 'move';
}

/** A press on an arrow that works an object's own handle (a cage point's gizmo,
 *  moveGizmo.ts): its figure slides along the arrow (`HeightAxis.along`) from
 *  `origin`, 0 at the press, worked as the object's handles are (HANDLE_RULE):
 *  a click arms it, a figure typed sets it, Esc puts it back, one undo step. */
export function startAxisWork3d(hd: AnyHandle, origin: Pt3, g: GizmoArrow, e: PointerEvent): void {
  const v = viewer3d, t = handleTarget(), d = hd.height?.along;
  if (!v || !t || !d) return;
  const tip = v.project({ x: origin.x + d.x * 100, y: origin.y + d.y * 100, z: origin.z + d.z * 100 });
  const ray = v.rayAt(e.clientX, e.clientY);
  startHandleWork({
    space: '3d', ref: { kind: t.kind, id: t.obj.id, part: t.part }, hd, rule: HANDLE_RULE, pick: 0, at: { x: origin.x, y: origin.y },
    press: { x: e.clientX, y: e.clientY }, alt: e.altKey, planOk: false,
    up: tip ? { x: tip.x - g.from.x, y: tip.y - g.from.y } : { x: 0, y: 0 },
    pxPerMm: Math.max(1e-3, tip ? Math.hypot(tip.x - g.from.x, tip.y - g.from.y) / 100 : 0), planPxPerMm: 1,
    zRef: rayToAxis(ray.o, ray.d, origin, d), planRef: null, viewSnaps: [],
  });
  v.setHover(null);
  $('view3d-canvas').style.cursor = 'move';
}

/** Paint the 3D view's handles on its overlay (`c`, css px; view3d.ts's
 *  `paintOverlay3d`, after every frame drawn) — the move gizmo first, under them
 *  (moveGizmo.ts `paintGizmo3d`) — and list them for the web cases: `key@x,y`,
 *  the worked one, what it reads. */
export function paintView3dHandles(c: CanvasRenderingContext2D, cv: HTMLCanvasElement): void {
  const wCss = cv.clientWidth, hCss = cv.clientHeight;
  const list = view3dHandles();
  const w = handleWork?.space === '3d' ? handleWork : null;
  const hot = w ? list.find((m) => m.hd.key === w.hd.key) : undefined;
  const t = w ? workedTarget(w) : null;
  // The gizmo's work's chip stands where the gizmo says (its arrow's tip, the ring).
  const tip = paintGizmo3d(c, cv, w);
  let live: HandleLive | null = null;
  if (tip && w && t) live = { text: w.rule.readout(w, t), at: tip };
  else if (w && hot && t && viewer3d) {
    // The vertical guide rises from what the figure is measured from.
    // (A section's slides along its normal, from where it stood.)
    const from = w.mode === 'plan' || w.hd.height?.along
      ? viewer3d.project({ x: w.at.x, y: w.at.y, z: w.hd.z })
      : viewer3d.project({ x: hot.p.x, y: hot.p.y, z: figureZero(w.hd) });
    live = { guide: from ? [from, hot.at] : undefined, text: w.rule.readout(w, t), at: hot.at };
  }
  const target = w ? t : handleTarget();
  const faint = list.length && target && viewer3d ? targetFaintGuides(target) : [];
  if (list.length && target && viewer3d) {
    const v = viewer3d;
    drawGuideLines(c, faint.map((line) => line.map((p) => v.project(p))), true);
    drawGuideLines(c, targetGuides(target).map((line) => line.map((p) => v.project(p))));
  }
  cv.dataset.faintGuides = String(faint.length);
  drawHandleMarks(c, list.map((m) => ({ at: m.at, hot: m === hot || !!m.hd.lit })), live, wCss, hCss);
  cv.dataset.handles = list.map((m) => `${m.hd.key}@${Math.round(m.at.x)},${Math.round(m.at.y)}`).join(' ');
  cv.dataset.hot = w ? `${w.hd.key}${w.armed ? ':armed' : ''}` : '';
  cv.dataset.readout = live ? live.text.join(' | ') : '';
}

/** The press the 3D pointer gives a handle's work (pointer3d.ts): an armed
 *  work's drop where it is, or the start of a handle's, an arrow's or the
 *  ring's work. */
export function pressWork3d(hit: Pointer3dHit, e: PointerEvent): void {
  if (hit.kind === 'armedHandle') HANDLE_UNDER_WAY.press({ space: '3d', at: { x: e.clientX, y: e.clientY }, alt: e.altKey });
  else if (hit.kind === 'handle') startHandle3d(hit.handle, e);
  else if (hit.kind === 'arrow') startArrowWork3d(hit.arrow, e);
  else if (hit.kind === 'ring') startRingWork3d(e);
}

/** A 3D work's step, once a frame (`Viewer3D.onFrame`): the latest pointer applied. */
export function stepWork3d(): void {
  if (handleWork?.space === '3d') stepHandleWork();
}

/** The pointer, as a 3D work under way reads it (its row's move). */
function trackWork3d(e: PointerEvent): void {
  HANDLE_UNDER_WAY.move?.({ space: '3d', at: { x: e.clientX, y: e.clientY }, alt: e.altKey });
}

/** A 3D work's pointer, on the overlay (its press is the 3D pointer's,
 *  ui/pointer3dUi.ts): it follows the pointer, a release that did not move arms
 *  it, and a lost pointer puts what holds it back (the gesture under way's). */
export function installView3dHandles(overlay: HTMLElement): void {
  overlay.addEventListener('pointermove', trackWork3d);
  overlay.addEventListener('pointerup', (e) => {
    const w = handleWork;
    if (w?.space !== '3d' || w.armed) return;
    if (overlay.hasPointerCapture(e.pointerId)) overlay.releasePointerCapture(e.pointerId);
    trackWork3d(e);
    if (!w.moved && Math.hypot(w.pointer.x - w.press.x, w.pointer.y - w.press.y) < DECIDE_PX) {
      w.armed = true; // a click: it follows the pointer until a click drops it
      w.dirty = false;
      updateStatus();
      invalidate();
      viewer3d?.redraw();
      return;
    }
    stepHandleWork();
    finishHandleWork();
  });
  overlay.addEventListener('pointercancel', () => { cancelHeldUnderWay(); });
}

// -- In the plan's section views --

/** A handle in a section view as drawn: its place in the view's frame, and on screen. */
export interface HandleInSection { hd: AnyHandle; sec: SectionLine; iv: InView; at: Vec2 }

/** Where the target's handles stand in each section view showing it (view frame),
 *  built once per state of what they read and of the sections. */
let viewHandlesOf: { key: string; list: Array<Omit<HandleInSection, 'at'> & { wp: Vec2 }> } | null = null;

/** The section views' handles as drawn now — the ONE list the plan paints and a
 *  press hits: each of the target's handles in every view that shows it (a room's
 *  feature on a partition: or its partition, which draws a doorway's header), on
 *  its figure where the view shows it (`handleInView`). */
function sectionHandles(): HandleInSection[] {
  if (state.viewTab !== 'model' || activeSheet()) return [];
  const w = handleWork?.space === 'section' ? handleWork : null;
  const t = w ? workedTarget(w) : handleTarget();
  if (!t || t.kind === 'section3d' || !(state.drawing.sections || []).length) return [];
  const hds = targetHandles(t);
  const key = `${handlesMade}|${w?.sectionId ?? ''}|${drawingRev()}`;
  if (viewHandlesOf?.key !== key) {
    const sv = sectionSources();
    const list: Array<Omit<HandleInSection, 'at'> & { wp: Vec2 }> = [];
    const id = t.obj.id;
    const host = t.kind === 'inst' && t.part ? featureHost(t.obj, t.part) : undefined;
    const shows = [srcOf(id, t.part?.kind, t.part?.id), ...(host ? [srcOf(id, 'partition', host)] : [])];
    const drawn = t.kind === 'inst' ? sv.instances.some((i) => i.id === id) : t.kind === 'wall' ? sv.walls.some((x) => x.id === id)
      : t.kind === 'conn' ? sv.pipes.some((p) => p.id === id) : sv.polygons.some((p) => p.id === id);
    if (drawn && hds.length) {
      for (const sec of state.drawing.sections || []) {
        // The view a handle is worked in keeps it while it is, even where the work
        // leaves the view nothing of its target (a door raised to its wall's top).
        if (objUnpickable(sec.id) || (w?.sectionId !== sec.id && !sectionSourceBoxes(sec, sv.instances, sv.walls, sv.polygons, shows, sv.pipes).some((b) => b))) continue;
        const { u, n } = sectionFrame(sec), plane: WorkPlane = { kind: 'section', sec };
        for (const { hd, iv } of handlesInView(hds, { a: sec.a, u, n })) list.push({ hd, sec, iv, wp: fromPlaneFrame(plane, { x: iv.t, y: iv.h }) });
      }
    }
    viewHandlesOf = { key, list };
  }
  return viewHandlesOf.list.map((m) => ({ hd: m.hd, sec: m.sec, iv: m.iv, at: vp.worldToScreen(m.wp) }));
}

/** The section-view handle under a screen point. */
export function sectionHandleAt(sp: Vec2): HandleInSection | null {
  if (state.tool !== 'select') return null;
  const list = sectionHandles();
  const i = handleMarkAt(list, sp);
  return i >= 0 ? list[i] : null;
}

/** The pointer in the worked section's view frame. */
function handleViewPoint(w: HandleWork): { t: number; h: number } | null {
  const sec = (state.drawing.sections || []).find((s) => s.id === w.sectionId);
  return sec ? sectionFramePoint(sec, vp.screenToWorld(w.pointer)) : null;
}

function handleHeightInView(w: HandleWork): number | null {
  const q = handleViewPoint(w);
  return q && w.zRef != null && w.hd.height ? w.hd.height.value + (q.h - w.zRef) : null;
}

/** A section's plan move: along its line, by how far the pointer went along the view. */
function handlePlanInView(w: HandleWork): Vec2 | null {
  const sec = (state.drawing.sections || []).find((s) => s.id === w.sectionId);
  const q = handleViewPoint(w);
  if (!sec || !q || !w.planRef) return null;
  const m = frameMove({ kind: 'section', sec }, { x: q.t - w.planRef.x, y: 0 });
  return { x: m.dx, y: m.dy };
}

/** A press on a section-view handle (`hit`, the mouse-down's `handleAt`): its
 *  work begins (a drag; a release that did not move arms it). True when it took
 *  the press. */
export function pressSectionHandle(e: MouseEvent, sp: Vec2, hit: HitOf<'viewHandle'> | null): boolean {
  const t = handleTarget();
  if (!hit || e.button !== 0 || handleWork || !t) return false;
  const m = hit.view;
  const view = sectionView(m.sec), zero = figureZero(m.hd), id = t.obj.id;
  const q = sectionFramePoint(m.sec, vp.screenToWorld(sp));
  const sv = sectionSources();
  // The view's other heights within reach: what the object does not draw there.
  const viewSnaps = sectionVertices(m.sec, sv.instances.filter((i) => i.id !== id), sv.walls.filter((x) => x.id !== id), sv.polygons.filter((p) => p.id !== id), sv.pipes.filter((p) => p.id !== id))
    .map((p) => sectionFramePoint(m.sec, p)).map((p) => ({ t: p.t, z: p.h - zero }));
  const o = vp.worldToScreen(view.O), up = vp.worldToScreen({ x: view.O.x + view.N.x * 100, y: view.O.y + view.N.y * 100 });
  startHandleWork({
    space: 'section', sectionId: m.sec.id, ref: { kind: t.kind, id, part: t.part }, hd: m.hd, rule: HANDLE_RULE, pick: 0, at: { x: m.iv.t, y: m.iv.h },
    press: sp, alt: e.altKey, planOk: m.iv.slides,
    up: { x: up.x - o.x, y: up.y - o.y }, pxPerMm: vp.scale, planPxPerMm: vp.scale,
    zRef: q.h, planRef: { x: q.t, y: 0 }, viewSnaps,
  });
  startDrag('handle');
  canvas.style.cursor = 'move';
  return true;
}

/** A section handle's drag let go (DRAG_LIFE `handle`'s end): a release that did
 *  not move arms it (it follows the pointer until a click drops it, its gesture
 *  still open); otherwise it is done. */
export function endSectionHandleDrag(e: MouseEvent): 'own' {
  dropDrag();
  const w = handleWork;
  if (w?.space === 'section' && !w.armed) {
    const sp = screenPos(e);
    w.pointer = sp;
    w.alt = e.altKey;
    if (!w.moved && Math.hypot(sp.x - w.press.x, sp.y - w.press.y) < DECIDE_PX) {
      w.armed = true;
      w.dirty = false;
      updateStatus();
      invalidate();
    } else {
      w.dirty = true;
      stepHandleWork();
      finishHandleWork();
    }
  }
  return 'own';
}

/** Paint the section views' handles over the plan (last, over everything), and
 *  list them on the canvas for the web cases: `section:key@x,y#t,h`. */
export function paintSectionHandles(): void {
  const list = sectionHandles();
  canvas.dataset.sectionHandles = list.map((m) => `${m.sec.id}:${m.hd.key}@${Math.round(m.at.x)},${Math.round(m.at.y)}#${m.iv.t.toFixed(1)},${m.iv.h.toFixed(1)}`).join(' ');
  const w = handleWork?.space === 'section' ? handleWork : null;
  canvas.dataset.handleHot = w ? `${w.sectionId}:${w.hd.key}${w.armed ? ':armed' : ''}` : '';
  if (!list.length) { canvas.dataset.handleReadout = ''; return; }
  const hot = w ? list.find((m) => m.sec.id === w.sectionId && m.hd.key === w.hd.key) : undefined;
  const t = w ? workedTarget(w) : null;
  let live: HandleLive | null = null;
  if (w && hot && t) {
    const view = sectionView(hot.sec);
    const at = (tt: number, h: number): Vec2 => vp.worldToScreen({ x: view.O.x + view.T.x * tt + view.N.x * h, y: view.O.y + view.T.y * tt + view.N.y * h });
    // Heights from what the figure is measured from (the guide rises from there).
    const zero = figureZero(w.hd);
    live = {
      guide: w.mode === 'plan' ? [at(w.at.x, hot.iv.h), hot.at] : [at(hot.iv.t, zero), hot.at],
      text: handleReadout(w, t), at: hot.at,
    };
    // A height the view draws, caught: a level guide to the nearest point at it.
    const c = w.caught;
    if (c && !c.name && w.mode === 'height') {
      const near = w.viewSnaps.filter((s) => Math.abs(s.z - c.z) < 1e-6).sort((a, b) => Math.abs(a.t - hot.iv.t) - Math.abs(b.t - hot.iv.t))[0];
      if (near) live.snapGuide = [at(near.t, zero + c.z), hot.at];
    }
  }
  canvas.dataset.handleReadout = live ? live.text.join(' | ') : '';
  const dpr = canvas.width / (viewW() || 1);
  ctx.save();
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  const target = t ?? handleTarget();
  const guides = target ? targetGuides(target) : [];
  const faint = guides.length && target ? targetFaintGuides(target) : [];
  if (guides.length) {
    for (const sec of new Set(list.map((m) => m.sec))) {
      const { u, n } = sectionFrame(sec), plane: WorkPlane = { kind: 'section', sec };
      const dep = (p: Pt3): number => (p.x - sec.a.x) * n.x + (p.y - sec.a.y) * n.y;
      const on = (p: Pt3): Vec2 => vp.worldToScreen(fromPlaneFrame(plane, { x: (p.x - sec.a.x) * u.x + (p.y - sec.a.y) * u.y, y: p.z }));
      // A line wholly behind the view's line is not what the view shows.
      const shown = (lines: Pt3[][]): Array<Array<Vec2 | null>> => lines.flatMap((line) => line.slice(1).map((q, i): Array<Vec2 | null> => (dep(line[i]) < -1 && dep(q) < -1 ? [] : [on(line[i]), on(q)])));
      drawGuideLines(ctx, shown(faint), true);
      drawGuideLines(ctx, shown(guides));
    }
  }
  drawHandleMarks(ctx, list.map((m) => ({ at: m.at, hot: m === hot || !!m.hd.lit })), live, viewW(), canvas.clientHeight || canvas.height / dpr);
  ctx.restore();
}
